import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../fetches/AuthorizationFetch';

// Create a context for authentication
const AuthContext = createContext(null);

/**
 * Provider component that wraps the entire application
 * and provides authentication state and functions
 */
export const AuthProvider = ({ children }) => {
  const auth = useAuth();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const navigate = useNavigate();

  // Function to fetch user profile with timeout
  const fetchUserProfile = async () => {
    if (!auth.accessToken) {
      setUser(null);
      setIsLoading(false);
      setFetchAttempted(true);
      return;
    }

    try {
      setIsLoading(true);
      
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      console.log('Fetching user profile...');
      const response = await auth.authenticatedFetch(
        `${process.env.REACT_APP_BASE_URL}/users/profile`,
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        console.warn(`Profile fetch failed with status: ${response.status}`);
        setUser(null);
        auth.logout();
        navigate('/login');
      }
    } catch (error) {
      // Handle different error types
      if (error.name === 'AbortError') {
        console.error('Profile fetch timed out after 10 seconds');
      } else if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        console.error('Network error when fetching profile - server may be down or CORS issue');
      } else {
        console.error('Error fetching user profile:', error);
      }
      
      setUser(null);
      
      // Only logout and redirect if not a network/CORS error
      // This prevents logout loops when backend is down
      if (!error.message.includes('NetworkError') && !error.message.includes('Failed to fetch')) {
        auth.logout();
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
      setFetchAttempted(true);
    }
  };

  // Check authentication status on mount and token changes
  useEffect(() => {
    fetchUserProfile();
    
    // Safety timeout to prevent infinite loading state
    const safetyTimeout = setTimeout(() => {
      if (isLoading) {
        console.warn('Authentication check timed out - forcing completion');
        setIsLoading(false);
        setFetchAttempted(true);
      }
    }, 15000); // 15 seconds max loading time
    
    return () => clearTimeout(safetyTimeout);
  }, [auth.accessToken]);

  // Handle login with email and password
  const login = async (email, password) => {
    try {
      await auth.login(email, password);
      await fetchUserProfile();
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  // Handle logout
  const logout = () => {
    auth.logout();
    setUser(null);
    navigate('/login');
  };

  // Context value
  const value = {
    user,
    isAuthenticated: !!auth.accessToken,
    isLoading,
    fetchAttempted,
    login,
    logout,
    refreshProfile: fetchUserProfile,
    ...auth, // Include original auth functions
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the authentication context
 */
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};